import type { NiatSource, Pilar } from '@iqrapath/shared'
import { useQuery, type QueryClient } from '@tanstack/react-query'
import type { ObjId } from '../../components/Obj.tsx'
import { api } from '../../lib/api.ts'
import type { Passage } from '../pegangan/pegangan.ts'

/** Mirrors apps/api/src/routes/perjalanan.ts. */

export type Garden = { light: number; level: number; levelUp: { from: number; to: number } | null }
export type Habit = { key: string; label: string; pilar: Pilar; custom: boolean }
export type HabitDay = Habit & { today: boolean; week: boolean[] }
export type Niat = { id: string; body: string; source: NiatSource; sourceRef: string | null; link: string | null; createdAt: string; doneAt: string | null }
export type RunState = { runId: string; runNo: number; startedOn: string; done: number[]; next: number | null; openToday: boolean; finished: boolean }
export type ProgramItem = { id: string; judul: string; ringkas: string; pilar: Pilar; tags: string[]; obj: string; reviewed: boolean; run: RunState | null }
export type KompasPilar = { pilar: Pilar; label: string; score: number; tahap: string }
export type Saran = { kind: 'kebiasaan'; key: string; label: string } | { kind: 'tantangan'; id: string; judul: string } | { kind: 'umum' }
export type Hadis = { id: string; text: string; ar: string | null; attribution: string; grade: string; url: string; source: string }
export type Plan = { berjuang: string[]; jadi: string | null; menit: number | null }

export type Hub = {
  today: string
  malam: boolean
  jumat: boolean
  garden: { light: number; level: number }
  kebiasaan: { days: string[]; habits: HabitDay[]; missedYesterday: boolean }
  muhasabah: { done: boolean }
  niat: { open: Niat[]; openCount: number }
  tantangan: { aktif: ProgramItem[]; tersedia: number }
  kompas: { pilar: KompasPilar[]; lemah: Pilar; saran: Saran }
  rencana: Plan | null
}
export type HabitsView = { days: string[]; habits: HabitDay[]; all: Habit[]; customCount: number; missedYesterday: boolean }
export type Reminder = { enabled: boolean; hour: number; minute: number }
export type MuhasabahView = {
  today: { jalan: Jalan | null; syukur: string | null; perbaiki: string | null } | null
  syukurLalu: { day: string; text: string }[]
  hadis: Hadis | null
  pengingat: Reminder
}
export type Jalan = 'ya' | 'sebagian' | 'belum'
export type ProgramDetail = { id: string; judul: string; ringkas: string; pilar: Pilar; obj: string; catatan: string | null; reviewed: boolean; days: { no: number; ref: string }[]; run: RunState | null }
export type DayView = {
  program: { id: string; judul: string; obj: string; catatan: string | null; reviewed: boolean }
  day: { no: number; ayat: Passage; tafsir: string | null; tafsirUrl: string; tantangan: string; catatan: string; refleksi: string; done: boolean; jawaban: string | null }
  run: RunState
}
export type Kompas = { from: string; to: string; pilar: KompasPilar[]; lemah: Pilar; saran: Saran; dasar: { ref: string; url: string; terjemah: string | null } }
export type Jumat = {
  from: string
  to: string
  jumat: boolean
  kebiasaan: { checks: number; possible: number; pct: number }
  niatSelesai: number
  malamMuhasabah: number
  hariTantangan: number
  tumbuh: { pilar: Pilar; label: string } | null
  syukur: { day: string; text: string }[]
  bekal: { id: string; judul: string; ringkas: string; ref: string } | null
  hadis: Hadis | null
  sepi: boolean
}
export type Rencana = { plan: Plan | null; rekomendasi: { tantangan: { id: string; judul: string; obj: string; run: RunState | null }[]; kebiasaan: { key: string; label: string; active: boolean }[] } }

const P = '/api/perjalanan'
export const PJ_KEY = ['perjalanan'] as const
const ok = <T>(path: string) => () => api<{ ok: true } & T>('GET', `${P}${path}`)

export const useHub = () => useQuery({ queryKey: PJ_KEY, queryFn: ok<Hub>('') })
export const useHabits = () => useQuery({ queryKey: [...PJ_KEY, 'kebiasaan'], queryFn: ok<HabitsView>('/kebiasaan') })
export const useMuhasabah = () => useQuery({ queryKey: [...PJ_KEY, 'muhasabah'], queryFn: ok<MuhasabahView>('/muhasabah') })
export const useNiat = () => useQuery({ queryKey: [...PJ_KEY, 'niat'], queryFn: ok<{ open: Niat[]; done: Niat[] }>('/niat') })
export const usePrograms = () => useQuery({ queryKey: [...PJ_KEY, 'tantangan'], queryFn: ok<{ programs: ProgramItem[]; aktif: ProgramItem[] }>('/tantangan') })
export const useProgram = (id: string) => useQuery({ queryKey: [...PJ_KEY, 'tantangan', id], queryFn: ok<ProgramDetail>(`/tantangan/${id}`), retry: false })
export const useDay = (id: string, hari: number) => useQuery({ queryKey: [...PJ_KEY, 'tantangan', id, hari], queryFn: ok<DayView>(`/tantangan/${id}/${hari}`), retry: false })
export const useKompas = () => useQuery({ queryKey: [...PJ_KEY, 'kompas'], queryFn: ok<Kompas>('/kompas') })
export const useJumat = () => useQuery({ queryKey: [...PJ_KEY, 'jumat'], queryFn: ok<Jumat>('/jumat') })
export const useRencana = () => useQuery({ queryKey: [...PJ_KEY, 'rencana'], queryFn: ok<Rencana>('/rencana') })

export const checkHabit = (key: string, done: boolean) => api<{ ok: true; done: boolean; garden: Garden }>('POST', `${P}/kebiasaan/centang`, { key, done })
export const setActiveHabits = (active: string[]) => api<{ ok: true }>('PUT', `${P}/kebiasaan`, { active })
export const addHabit = (label: string, pilar: Pilar) => api<{ ok: true; habit: Habit }>('POST', `${P}/kebiasaan/buat`, { label, pilar })
export const removeHabit = (id: string) => api<{ ok: true }>('POST', `${P}/kebiasaan/hapus`, { id })
export const saveMuhasabah = (e: { jalan: Jalan | null; syukur: string | null; perbaiki: string | null }) => api<{ ok: true; garden: Garden }>('PUT', `${P}/muhasabah`, e)
export const putMuhasabahReminder = (r: Reminder) => api<{ ok: true; pengingat: Reminder }>('PUT', `${P}/muhasabah/pengingat`, r)
export const addNiat = (body: string, source: NiatSource = 'sendiri', sourceRef: string | null = null) => api<{ ok: true; niat: Niat }>('POST', `${P}/niat`, { body, source, sourceRef })
export const setNiatDone = (id: string, done: boolean) => api<{ ok: true; niat: Niat; garden: Garden }>('POST', `${P}/niat/selesai`, { id, done })
export const deleteNiat = (id: string) => api<{ ok: true }>('POST', `${P}/niat/hapus`, { id })
export const startProgram = (id: string) => api<{ ok: true; run: RunState }>('POST', `${P}/tantangan/${id}/mulai`)
export const completeDay = (id: string, hari: number, refleksi: string | null) =>
  api<{ ok: true; run: RunState; garden: Garden; firstTime: boolean }>('POST', `${P}/tantangan/${id}/${hari}/selesai`, { refleksi })

/** Everything under Perjalanan may change after an action (light, compass, the hub): refetch it all. */
export const refreshPerjalanan = (qc: QueryClient) => qc.invalidateQueries({ queryKey: PJ_KEY })

export const PILAR_OBJ: Record<Pilar, ObjId> = { iman: 'lampu', amal: 'kendi', kebenaran: 'kompas', sabar: 'jam-pasir' }
export const JALAN_LABEL: Record<Jalan, string> = { ya: 'Ya', sebagian: 'Sebagian', belum: 'Belum' }

const dayFmt = new Intl.DateTimeFormat('id-ID', { weekday: 'short', timeZone: 'UTC' })
const dateFmt = new Intl.DateTimeFormat('id-ID', { day: 'numeric', month: 'long', timeZone: 'UTC' })
/** 'YYYY-MM-DD' (a WIB calendar day) → "Sen", "Sel", … */
export const dayShort = (ymd: string) => dayFmt.format(new Date(`${ymd}T00:00:00Z`)).replace('.', '')
export const dayLong = (ymd: string) => dateFmt.format(new Date(`${ymd}T00:00:00Z`))
/** 'YYYY-MM-DD' (the server's WIB day) is a Friday */
export const isFriday = (ymd: string) => new Date(`${ymd}T00:00:00Z`).getUTCDay() === 5
